import React, { useEffect, useRef, useState } from 'react'

import { formatNumber, resizeObserverHandler } from '../../utils'
import { AlarmDescription, ConditionPill, ConditionPillsContainer, MustSave } from '../styles/card'

const formatCondition = (
    { channel, comparator, threshold, uom }: Condition,
    selectedUnitSet: UnitSet,
    curves: Curves,
    rigStateOutputs: RigStateOutputs,
    includeUom = false
): string => {
    if (!channel || !comparator || (!threshold && threshold !== 0)) return ''

    const comparatorText =
        comparator === 'in range' ? 'between' : comparator === 'outside range' ? 'not between' : comparator
    const description = selectedUnitSet?.data?.[channel]?.description || curves?.[channel]?.description || channel
    const numbers = (Array.isArray(threshold) ? threshold : [threshold]).map(
        val => formatNumber(val, null, null) + (includeUom ? ' ' + uom : '')
    )
    const rigStates =
        channel === 'rigstate'
            ? (threshold as string[]).map(v => {
                  const rigState = rigStateOutputs.find(o => o.id === v)

                  return rigState?.overrideName || rigState?.name
              })
            : null

    return `${description} ${comparatorText} ${channel === 'rigstate' ? `[${rigStates}]` : numbers.join(' and ')}`
}

type BaseDescriptionProps = {
    conditions: Condition[]
    curves: Curves
    description: string
    selectedUnitSet: UnitSet
    rigStateOutputs: RigStateOutputs
}

type DescriptionProps = BaseDescriptionProps & {
    collapsed: boolean
    index: number
    mustSave: boolean
    newRule: boolean
    sideview?: boolean
}

// used just for calculations
const DescriptionHiddenElements = ({
    conditions,
    curves,
    rigStateOutputs,
    description,
    elementRef,
    selectedUnitSet
}: BaseDescriptionProps & { elementRef: React.Ref<HTMLDivElement> }): React.ReactElement => (
    <div ref={elementRef} style={{ display: 'flex', position: 'absolute', bottom: 0 }}>
        <AlarmDescription hidden={true}>{description}</AlarmDescription>
        <ConditionPillsContainer hidden={true}>
            {conditions
                .filter(({ channel, comparator, threshold }) => channel && comparator && (threshold || threshold === 0))
                .map((condition, idx) => (
                    <ConditionPill key={idx} withUom={!!condition.uom}>
                        <span className="alarms--card-description--pill--condition">
                            {formatCondition(condition, selectedUnitSet, curves, rigStateOutputs)}
                        </span>
                        {condition.uom}
                    </ConditionPill>
                ))}
            <ConditionPill withUom={false}>+2</ConditionPill>
        </ConditionPillsContainer>
    </div>
)

const Description = ({
    collapsed,
    conditions,
    curves,
    description,
    index,
    mustSave,
    newRule,
    selectedUnitSet,
    sideview,
    rigStateOutputs
}: DescriptionProps): React.ReactElement => {
    const ref = useRef(null)
    const [clipText, setClipText] = useState(false)
    const [numberOfPills, setNumberOfPills] = useState(0)
    const [showPills, setShowPills] = useState(true)

    const finalDescription = description || (newRule ? 'New rule' : 'Rule') + ' - #' + (index + 1)

    useEffect(() => {
        const observer = new ResizeObserver(([observed]: ResizeObserverEntry[]) =>
            resizeObserverHandler(observed, sideview, mustSave, collapsed, setNumberOfPills, setShowPills, setClipText)
        )

        const refCurrent = ref.current
        observer.observe(refCurrent.parentNode)

        return () => {
            if (refCurrent) observer.unobserve(refCurrent.parentNode)
        }
    }, [sideview, mustSave, collapsed, conditions])

    return (
        <>
            <AlarmDescription
                title={
                    description ||
                    conditions.map(c => formatCondition(c, selectedUnitSet, curves, rigStateOutputs, true)).join(', ')
                }
                clipText={clipText}
            >
                {finalDescription}
            </AlarmDescription>
            {showPills && (
                <ConditionPillsContainer>
                    {conditions
                        .slice(0, numberOfPills)
                        .filter(
                            ({ channel, comparator, threshold }) =>
                                channel &&
                                comparator &&
                                (Array.isArray(threshold) ? threshold : [threshold]).every(val => val || val === 0)
                        )
                        .map((condition, idx) => (
                            <ConditionPill key={idx} withUom={!!condition.uom}>
                                <span className="alarms--card-description--pill--condition">
                                    {formatCondition(condition, selectedUnitSet, curves, rigStateOutputs)}
                                </span>
                                {condition.uom}
                            </ConditionPill>
                        ))}
                    {numberOfPills && numberOfPills < conditions.length ? (
                        <ConditionPill
                            title={conditions
                                .slice(numberOfPills)
                                .map(condition =>
                                    formatCondition(condition, selectedUnitSet, curves, rigStateOutputs, true)
                                )
                                .filter(t => t)
                                .join(', ')}
                            withUom={false}
                        >
                            +{conditions.length - numberOfPills}
                        </ConditionPill>
                    ) : null}
                </ConditionPillsContainer>
            )}
            {mustSave && collapsed && <MustSave>There are unsaved changes</MustSave>}

            <DescriptionHiddenElements
                conditions={conditions}
                curves={curves}
                rigStateOutputs={rigStateOutputs}
                description={finalDescription}
                elementRef={ref}
                selectedUnitSet={selectedUnitSet}
            />
        </>
    )
}

export default Description
